import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

// Chart config hoisted to module scope: never changes, avoids re-creating on
// every render and prevents unnecessary Recharts child re-renders.
const CHART_MARGIN = { top: 8, right: 16, left: -8, bottom: 0 };
const GRID_STROKE = "rgba(255,255,255,0.06)";
const AXIS_LINE = { stroke: "rgba(255,255,255,0.1)" };
const XAXIS_TICK = { fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono" };
const YAXIS_LEFT_TICK = { fill: "#a1a1aa", fontSize: 10, fontFamily: "JetBrains Mono" };
const YAXIS_RIGHT_TICK = { fill: "#06b6d4", fontSize: 10, fontFamily: "JetBrains Mono" };
const TOOLTIP_CONTENT_STYLE = { background: "#18181B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 };
const TOOLTIP_LABEL_STYLE = { color: "#A3E635" };
const TOOLTIP_ITEM_STYLE = { padding: 0 };
const LEGEND_WRAPPER_STYLE = { fontFamily: "JetBrains Mono", fontSize: 10, color: "#a1a1aa", paddingTop: 4 };

const tooltipFormatter = (value, name) => {
  if (name === "avg_price_sqm") return [`€${Math.round(value)}/m²`, "Vendita"];
  if (name === "avg_rent_sqm") return [`€${value}/m²`, "Affitto"];
  if (name === "yield_pct") return [`${value}%`, "Rendimento"];
  return [value, name];
};
const legendFormatter = (v) =>
  v === "avg_price_sqm" ? "Vendita €/m²" : v === "yield_pct" ? "Rend. %" : v;
const yLeftFormatter = (v) => `${Math.round(v / 100) / 10}k`;
const yRightFormatter = (v) => `${v}%`;

export default function TrendChart({ city, zone, months = 24, height = 200 }) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!city) return;
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ city, months: String(months) });
    if (zone) params.append("zone", zone);
    api.get(`/trend?${params.toString()}`)
      .then((r) => setSeries(r.data.series))
      .catch((e) => setError(e?.response?.data?.detail || "Trend non disponibile"))
      .finally(() => setLoading(false));
  }, [city, zone, months]);

  if (loading) return <div className="h-[200px] flex items-center justify-center font-mono-data text-xs text-lime-400 animate-pulse">CARICAMENTO TREND...</div>;
  if (error) return <div className="h-[120px] flex items-center justify-center font-mono-data text-xs text-red-400">{error}</div>;
  if (!series.length) return <div className="h-[120px] flex items-center justify-center font-mono-data text-xs text-zinc-500">Nessun dato di trend</div>;

  const xInterval = Math.max(0, Math.floor(series.length / 6) - 1);

  return (
    <div className="w-full" data-testid="trend-chart" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="2 4" stroke={GRID_STROKE} vertical={false} />
          <XAxis
            dataKey="month"
            tick={XAXIS_TICK}
            axisLine={AXIS_LINE}
            tickLine={false}
            interval={xInterval}
          />
          <YAxis
            yAxisId="left"
            tick={YAXIS_LEFT_TICK}
            axisLine={false}
            tickLine={false}
            width={45}
            tickFormatter={yLeftFormatter}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={YAXIS_RIGHT_TICK}
            axisLine={false}
            tickLine={false}
            width={35}
            tickFormatter={yRightFormatter}
          />
          <Tooltip
            contentStyle={TOOLTIP_CONTENT_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
            itemStyle={TOOLTIP_ITEM_STYLE}
            formatter={tooltipFormatter}
          />
          <Legend
            wrapperStyle={LEGEND_WRAPPER_STYLE}
            iconType="line"
            formatter={legendFormatter}
          />
          <Line yAxisId="left" type="monotone" dataKey="avg_price_sqm" stroke="#A3E635" strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line yAxisId="right" type="monotone" dataKey="yield_pct" stroke="#06B6D4" strokeWidth={1.5} strokeDasharray="4 2" dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
